/**
 * Shared prompt-building utilities for itinerary generation.
 * Used by both web and mobile to ensure prompt parity.
 *
 * Exports: calculateTravelStructure, buildCompressedContext, isTravelDay,
 *          getStopForDay, buildTravelDayInstructions, isVagueLocation, deduplicateDay
 */

// ─── Travel Structure ──────────────────────────────────────────────────────

/**
 * Calculate travel structure for multi-stop trips.
 * Standalone version that works without React state.
 *
 * @param {Array} stops - Array of stop objects { name, days, resolvedLocation, ... } or strings
 * @param {number} totalDays - Total trip days
 * @param {Object} [options] - Optional context
 * @param {string} [options.homeCity] - User's home/departure city
 * @param {string} [options.returnCity] - User's return city (if different from homeCity = open-jaw)
 * @returns {{ stops: Array, travelDays: Array, isMultiStop: boolean, tripPattern: string, isRoundTrip: boolean, isOpenJaw: boolean, firstCity: string, lastCity: string, departureCity: string, returnCity: string, flightNote: string|null }}
 */
export function calculateTravelStructure(stops, totalDays, options = {}) {
  if (!stops || stops.length <= 1) {
    const singleName = typeof stops?.[0] === 'string' ? stops[0] : stops?.[0]?.resolvedLocation || stops?.[0]?.name || '';
    return {
      stops: singleName ? [{ name: singleName, startDay: 1, endDay: totalDays, days: totalDays }] : [],
      travelDays: [],
      isMultiStop: false,
      tripPattern: 'single',
    };
  }

  const { homeCity, returnCity: optReturnCity } = options;

  // Phase 1: Calculate day allocation respecting user-specified days
  let userSpecifiedTotal = 0;
  let unspecifiedCount = 0;
  stops.forEach(stop => {
    const hasDays = typeof stop === 'object' && stop.days;
    if (hasDays) {
      userSpecifiedTotal += stop.days;
    } else {
      unspecifiedCount++;
    }
  });

  // If user-specified days exceed total, scale down proportionally
  if (userSpecifiedTotal > totalDays) {
    const scale = totalDays / userSpecifiedTotal;
    stops.forEach(stop => {
      if (typeof stop === 'object' && stop.days) {
        stop.days = Math.max(1, Math.round(stop.days * scale));
      }
    });
    // Recalculate after scaling
    userSpecifiedTotal = 0;
    stops.forEach(stop => {
      if (typeof stop === 'object' && stop.days) userSpecifiedTotal += stop.days;
    });
    // Clamp: Math.round can overshoot when multiple stops land on .5
    while (userSpecifiedTotal > totalDays) {
      const longest = stops.reduce((a, b) =>
        (typeof a === 'object' && a.days > 1 && a.days >= (b?.days || 0)) ? a : b
      );
      if (typeof longest === 'object' && longest.days > 1) {
        longest.days--;
        userSpecifiedTotal--;
      } else break;
    }
  }

  // Phase 2: Distribute remaining days among unspecified stops
  const remainingDays = totalDays - userSpecifiedTotal;
  const daysPerUnspecified = unspecifiedCount > 0 ? Math.floor(remainingDays / unspecifiedCount) : 0;
  const extraDays = unspecifiedCount > 0 ? remainingDays % unspecifiedCount : 0;
  // Fallback for all-specified case
  const daysPerStop = Math.floor(totalDays / stops.length);

  // Determine trip pattern: round-trip (A→B→A) vs open-jaw (A→B→C)
  // Use resolvedLocation (geographic city) when available for accurate comparison
  const getStopName = (s) => typeof s === 'string' ? s.toLowerCase() : (s.resolvedLocation || s.name)?.toLowerCase();
  const firstStop = getStopName(stops[0]);
  const lastStop = getStopName(stops[stops.length - 1]);

  // Check user's explicit return city preference
  const departureCity = homeCity?.toLowerCase();
  const returnCityLower = optReturnCity?.toLowerCase();

  // If user explicitly set a different return city, it's an open-jaw trip
  const userSetOpenJaw = returnCityLower && returnCityLower !== departureCity;

  // Check if first and last stops are the same city/country (for destination-based detection)
  const destinationRoundTrip = firstStop && lastStop && (
    firstStop === lastStop ||
    firstStop.includes(lastStop) ||
    lastStop.includes(firstStop)
  );

  // Open-jaw if user explicitly set it OR if destinations suggest it
  const isOpenJaw = userSetOpenJaw || (!destinationRoundTrip && stops.length >= 2);
  const isRoundTrip = !isOpenJaw;

  // Build flight note with explicit cities
  let flightNote = null;
  if (isOpenJaw) {
    const flyFrom = homeCity || 'your city';
    const flyTo = optReturnCity || (stops[stops.length - 1]?.name || stops[stops.length - 1]);
    const firstDest = stops[0]?.name || stops[0];
    const lastDest = stops[stops.length - 1]?.name || stops[stops.length - 1];

    if (userSetOpenJaw) {
      flightNote = `Open-jaw trip: Fly ${flyFrom} → ${firstDest}, return ${lastDest} → ${flyTo}. Book as multi-city flight for best rates.`;
    } else {
      flightNote = `Open-jaw trip: Fly into ${firstDest}, fly out of ${lastDest}. Book as multi-city flight for best rates.`;
    }
  }

  const structure = {
    stops: [],
    travelDays: [],
    isMultiStop: true,
    tripPattern: isRoundTrip ? 'round-trip' : 'open-jaw',
    isRoundTrip,
    isOpenJaw,
    firstCity: firstStop,
    lastCity: lastStop,
    departureCity: homeCity || null,
    returnCity: optReturnCity || homeCity || null,
    flightNote,
  };

  let currentDay = 1;
  let unspecifiedIdx = 0;
  stops.forEach((stop, i) => {
    const isLast = i === stops.length - 1;
    // Use user-specified days if available, otherwise distribute remaining days evenly
    const hasUserDays = typeof stop === 'object' && stop.days;
    let stopDays;
    if (hasUserDays) {
      stopDays = stop.days;
    } else if (isLast) {
      // Last unspecified stop absorbs remaining days to prevent rounding errors
      stopDays = Math.max(1, totalDays - currentDay + 1);
    } else if (unspecifiedCount > 0) {
      // Give base allocation + 1 extra day to early unspecified stops
      stopDays = Math.max(1, daysPerUnspecified + (unspecifiedIdx < extraDays ? 1 : 0));
      unspecifiedIdx++;
    } else {
      stopDays = daysPerStop;
    }

    // Use resolvedLocation (geographic name) when available for transport/scheduling
    const stopName = typeof stop === 'string' ? stop : (stop.resolvedLocation || stop.name);

    structure.stops.push({
      name: stopName,
      startDay: currentDay,
      endDay: currentDay + stopDays - 1,
      days: stopDays,
      userSpecifiedDays: !!hasUserDays,
      includesArrival: i === 0,
      includesDeparture: isLast,
      resolvedLocation: typeof stop === 'object' ? stop.resolvedLocation : null,
      originalName: typeof stop === 'object' ? stop.name : stop,
      stopType: typeof stop === 'object' ? stop.stopType : null,
      hotelName: typeof stop === 'object' ? stop.hotelName : null,
      // Propagate primaryCityHint and id so downstream consumers
      // (getStopForDay → orchestrator:5234, 5992; userMessageBuilder:386;
      // userPrefs.stopHotels lookup) can read them. Stripping these
      // silently broke the bare-country fix and per-stop hotel mapping.
      primaryCityHint: typeof stop === 'object' ? stop.primaryCityHint : null,
      id: typeof stop === 'object' ? stop.id : null,
    });

    // Travel day = LAST day of the CURRENT stop (departure day)
    // The traveler departs from the current city on this day and arrives at the next city
    // (same-day arrival or next-day arrival for overnight flights).
    // Arrow pills between day buttons should only appear for overnight flights.
    if (!isLast) {
      const nextStop = stops[i + 1];
      const nextStopName = typeof nextStop === 'string' ? nextStop : (nextStop.resolvedLocation || nextStop.name);
      structure.travelDays.push({
        day: currentDay + stopDays - 1, // Departure on last day of current stop
        from: stopName,
        to: nextStopName,
        type: 'inter-city-travel',
      });
    }

    currentDay += stopDays;
  });

  return structure;
}

/**
 * Build compressed context from previously generated days.
 * Used during progressive/batch generation to prevent duplicate activities,
 * restaurants, cuisines, and neighborhoods across days.
 *
 * @param {Array} completedDays - Array of previously generated day objects
 * @returns {Object} Compressed context with dedup info
 */
export function buildCompressedContext(completedDays) {
  if (!completedDays || completedDays.length === 0) {
    return {
      daysCompleted: [],
      usedActivities: [],
      usedRestaurants: [],
      neighborhoodsCovered: [],
      currentHotel: null,
    };
  }

  // Filter out any null/failed days
  const validDays = completedDays.filter(d => d !== null && d !== undefined);

  // Extract day trip destinations (border crossings, distant locations)
  const dayTripPatterns = ['border crossing', 'day trip', 'excursion to', 'visit to'];
  const countryIndicators = ['argentina', 'chile', 'peru', 'bolivia', 'brazil', 'mexico', 'spain', 'france', 'italy', 'germany'];

  const recentDayTrips = [];
  validDays.forEach((d) => {
    const dayActivities = (d.schedule || d.activities || []);
    dayActivities.forEach(a => {
      const activityLower = (a.activity || a.name || '').toLowerCase();
      const locationLower = (a.location || '').toLowerCase();

      // Check if this looks like a day trip
      const isDayTripActivity = dayTripPatterns.some(p => activityLower.includes(p));
      const hasDifferentCountry = countryIndicators.some(c =>
        (locationLower.includes(c) || activityLower.includes(c)) &&
        !d.baseCity?.toLowerCase().includes(c)
      );

      if (isDayTripActivity || hasDifferentCountry) {
        recentDayTrips.push({
          day: d.day,
          activity: a.activity || a.name,
          location: a.location,
        });
      }
    });
  });

  // Track activity types/themes across days for variety awareness
  const activityTypeCounts = {};
  const cuisinesCovered = [];
  validDays.forEach(d => {
    (d.schedule || d.activities || []).forEach(a => {
      const aType = (a.type || '').toLowerCase();
      if (aType) activityTypeCounts[aType] = (activityTypeCounts[aType] || 0) + 1;
    });
    (d.meals || []).forEach(m => {
      if (m.cuisine) cuisinesCovered.push(m.cuisine);
    });
  });

  return {
    daysCompleted: validDays.map(d => d.day),
    usedActivities: validDays.flatMap(d =>
      (d.schedule || d.activities || []).map(a => a.activity || a.name)
    ).filter(Boolean),
    usedRestaurants: validDays.flatMap(d =>
      (d.meals || []).map(m => m.restaurant)
    ).filter(Boolean),
    neighborhoodsCovered: [...new Set(
      validDays.map(d => d.neighborhood || d.baseCity).filter(Boolean)
    )],
    currentHotel: validDays[validDays.length - 1]?.hotel?.name || null,
    // Track recent day trips to avoid consecutive border crossings
    recentDayTrips,
    // Track activity type distribution for variety
    activityTypeCounts,
    cuisinesCovered: [...new Set(cuisinesCovered)],
    // Summary for each day (compact) - includes theme for variety tracking
    daySummaries: validDays.map(d => ({
      day: d.day,
      theme: d.theme || '',
      neighborhood: d.neighborhood || d.baseCity,
      highlights: (d.schedule || d.activities || []).slice(0, 2).map(a => a.activity) || [],
      types: [...new Set((d.schedule || d.activities || []).map(a => a.type).filter(Boolean))],
    })),
  };
}

/**
 * Check if a given day number is a travel day.
 */
export function isTravelDay(dayNumber, travelStruct) {
  if (!travelStruct?.travelDays) return null;
  return travelStruct.travelDays.find(t => t.day === dayNumber) || null;
}

/**
 * Get the stop for a given day number.
 */
export function getStopForDay(dayNumber, travelStruct) {
  if (!travelStruct?.stops) return null;
  return travelStruct.stops.find(s => dayNumber >= s.startDay && dayNumber <= s.endDay) || null;
}

// ─── Prompt Fragment Builders ──────────────────────────────────────────────

/**
 * Build travel day instructions for an inter-city travel day.
 *
 * @param {string} from - Departure city
 * @param {string} to - Arrival city
 * @param {Object} [transportData] - Pre-fetched transport { mode, duration, reason }
 * @param {boolean} [isDriving] - Whether this is a driving trip
 * @param {Object} [flightSchedule] - Pre-computed flight schedule from estimateFlightSchedule()
 * @returns {string} Prompt fragment with travel day instructions
 */
export function buildTravelDayInstructions(from, to, transportData = null, isDriving = false, flightSchedule = null) {
  let travelActivity;
  let travelModeLabel;
  let transitHint;

  if (isDriving) {
    travelActivity = `Drive from ${from} → ${to} (scenic road trip — look up actual driving time and mention it)`;
    travelModeLabel = 'drive';
    transitHint = 'the road';
  } else if (transportData?.mode && transportData?.duration) {
    const modeLabels = { train: 'Train', flight: 'Flight', car: 'Drive', ferry: 'Ferry', custom: 'Custom' };
    const label = modeLabels[transportData.mode] || transportData.mode;
    travelActivity = `${label} from ${from} → ${to} (${transportData.duration} — ${transportData.reason}). Use this EXACT mode, do not suggest an alternative.`;
    travelModeLabel = `${transportData.mode} (${transportData.duration})`;
    transitHint = transportData.mode === 'train' ? 'the train station' : transportData.mode === 'flight' ? 'the airport' : 'the station/airport';
  } else {
    travelActivity = `Travel from ${from} → ${to} — choose the most practical transport for this route. Prefer high-speed train for nearby cities (under 3 hours), only fly if 4+ hours by ground or different islands.`;
    travelModeLabel = 'choose the most practical option (train preferred for nearby cities)';
    transitHint = 'the station/airport';
  }

  // Compute concrete timing if flight schedule is available
  const hasFlightSchedule = flightSchedule && flightSchedule.departureTime && flightSchedule.arrivalTime;
  const flightScheduleLine = hasFlightSchedule
    ? `\n📋 FLIGHT SCHEDULE: Departs ~${flightSchedule.departureTime} → Arrives ~${flightSchedule.arrivalTime}${flightSchedule.isOvernight ? ' (NEXT DAY)' : ''} (${flightSchedule.duration} flight)`
    : '';

  // Calculate leave-for-airport time
  let leaveByTime = null;
  if (hasFlightSchedule) {
    const [h, m] = flightSchedule.departureTime.split(':').map(Number);
    const bufferMin = flightSchedule.durationMinutes >= 360 ? 180 : 120; // 3h intl, 2h domestic
    const leaveMin = Math.max(0, h * 60 + m - bufferMin);
    leaveByTime = `${String(Math.floor(leaveMin / 60)).padStart(2, '0')}:${String(leaveMin % 60).padStart(2, '0')}`;
  }

  const morningTimeConstraint = leaveByTime
    ? `- ⏰ LEAVE FOR ${transitHint} BY ${leaveByTime} — no activities after this time`
    : `- Leave time for hotel checkout and transit to ${transitHint}`;

  // Overnight flights: the traveler departs in the evening and arrives the NEXT day.
  // This day = departure day (last day at current stop). No evening activities in arrival city.
  const isOvernight = flightSchedule?.isOvernight;

  if (isOvernight) {
    return `
🚨 THIS IS A DEPARTURE DAY - ${from} → ${to} (OVERNIGHT ${isDriving ? 'DRIVE' : 'FLIGHT'})
The traveler departs ${from} tonight and arrives in ${to} TOMORROW.
Transport mode: ${travelModeLabel}${flightScheduleLine}

🏨 HOTEL RULE: The "hotel" can be "${from}" hotel (checkout day) OR omitted.
The "baseCity" must be "${from}" — the traveler spends the day here.

FULL DAY IN ${from.toUpperCase()} (before evening departure):
${morningTimeConstraint}
- Schedule 3-4 activities throughout the day in ${from}
- A proper sit-down breakfast at a local restaurant
- Lunch at a great local spot
- Include last-chance highlights or things NOT YET DONE in ${from}
- The traveler has most of the day before heading to the airport/station

EVENING DEPARTURE:
- ${travelActivity}
- Light dinner near the airport/station OR at the airport before boarding
- No activities in ${to} — the traveler arrives TOMORROW

TOTAL: This day should have 3-4 activities + 2-3 meals (breakfast, lunch, and possibly a light dinner before departure).
The evening is spent traveling. Do NOT schedule anything in ${to} for this day.`;
  }

  // Same-day travel: morning in departure city, evening in arrival city
  return `
🚨 THIS IS A TRAVEL DAY - ${from} → ${to}
This day has TWO halves - BOTH should have meaningful content:
Transport mode for this leg: ${travelModeLabel}${flightScheduleLine}

🏨 CRITICAL HOTEL RULE FOR TRAVEL DAYS:
- The "hotel" in your JSON must be a hotel in ${to.toUpperCase()} (the ARRIVAL city)
- The "baseCity" must be "${to}" (the city where the traveler sleeps tonight)
- Do NOT use a hotel from ${from} — the traveler is checking OUT of there today

MORNING IN ${from.toUpperCase()} (before departure):
${morningTimeConstraint}
- Schedule 1-2 activities (2-3 hours worth) in ${from}
- A proper sit-down breakfast at a local restaurant
- These should be quick highlights or things NOT YET DONE in ${from}

TRAVEL:
- ${travelActivity}
${isDriving ? '- If the drive is scenic, mention interesting stops along the way\n- Include the drive as an activity with realistic duration' : '- Do NOT include any activities during transit'}

EVENING IN ${to.toUpperCase()} (after arrival ~${hasFlightSchedule ? flightSchedule.arrivalTime : 'afternoon'}):
- Schedule 2-3 activities after arrival (neighborhood walk, landmark visit, local market, etc.)
- Include a proper dinner at a well-known restaurant in ${to}
- This is the traveler's FIRST TASTE of ${to} — make it count!
- Suggest an iconic area to explore on foot (main square, waterfront, old town)
- Consider adding a bar/nightlife suggestion or evening viewpoint

TOTAL: This day should have 3-5 activities + 3 meals (breakfast in ${from}, lunch during/after travel, dinner in ${to})`;
}

// ─── Vague Location Detection ───────────────────────────────────────────────

const ACTIVITY_KEYWORDS = [
  'skiing', 'ski', 'safari', 'beach', 'wine', 'hiking', 'diving', 'surfing',
  'trekking', 'countryside', 'coast', 'lake district', 'jungle', 'desert', 'volcano',
  'national park', 'ruins', 'temples', 'islands', 'mountains', 'snorkeling', 'kayaking',
];

/**
 * Detect if a stop name is vague/conceptual (needs LLM resolution to a real place).
 * e.g., "Japan Skiing" → true, "Tokyo" → false
 */
export function isVagueLocation(name) {
  if (!name) return false;
  const lower = name.toLowerCase();
  return !name.includes(',') && ACTIVITY_KEYWORDS.some(kw => lower.includes(kw));
}

// ─── Deduplication ──────────────────────────────────────────────────────────

/**
 * Fuzzy-match two activity titles. Catches near-duplicates the previous
 * exact-string check missed — e.g. "Grand Friday Mosque" vs "Visit Grand
 * Friday Mosque" (Day 1 + Day 2 of a Maldives trip surfaced this), or
 * "Cathedral of Light" vs "The Cathedral of Light".
 *
 * Approach (cheap → expensive):
 *   1. exact match
 *   2. substring containment (one fully contains the other, ≥10 chars)
 *   3. Levenshtein distance ≤ 3 on the longer of the two
 *   4. shared distinctive token after stripping common verbs
 */
function isFuzzyDuplicate(a, b) {
  if (!a || !b) return false;
  const x = a.toLowerCase().trim();
  const y = b.toLowerCase().trim();
  if (x === y) return true;
  // Substring (one fully inside the other, both reasonably long)
  if (x.length >= 10 && y.length >= 10 && (x.includes(y) || y.includes(x))) return true;
  // Strip leading verbs that the LLM tends to add inconsistently
  const STRIP = /^(visit|tour|explore|walk through|see|experience|enjoy|stop at|go to|the)\s+/i;
  const xs = x.replace(STRIP, '').replace(STRIP, '');
  const ys = y.replace(STRIP, '').replace(STRIP, '');
  if (xs === ys) return true;
  if (xs.length >= 10 && ys.length >= 10 && (xs.includes(ys) || ys.includes(xs))) return true;
  // Levenshtein distance
  const longer = xs.length > ys.length ? xs : ys;
  if (longer.length < 6) return false; // too short to safely fuzzy-match
  // Inline Levenshtein (avoid an import cycle)
  const m = xs.length, n = ys.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = xs[i - 1] === ys[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return dp[m][n] <= 3;
}

export function deduplicateDay(newDay, existingDays = []) {
  if (!newDay || !existingDays || existingDays.length === 0) return newDay;

  const existingActivityNames = existingDays.flatMap(d =>
    (d?.schedule || d?.activities || []).map(a => (a.activity || a.name || ''))
  );
  const existingRestaurantNames = existingDays.flatMap(d =>
    (d?.meals || []).map(m => (m.restaurant || ''))
  );

  const newActivities = newDay.schedule || newDay.activities || [];

  // Filter duplicates with fuzzy matching against ALL prior days. Catches
  // "Grand Friday Mosque" Day 1 vs "Visit Grand Friday Mosque" Day 2 — the
  // exact-string-match version of this check let it through.
  const dedupedActivities = newActivities.filter(a => {
    const actName = a.activity || a.name || '';
    return !existingActivityNames.some(prev => isFuzzyDuplicate(actName, prev));
  });

  const dedupedMeals = (newDay.meals || []).filter(m => {
    const restName = m.restaurant || '';
    return !existingRestaurantNames.some(prev => isFuzzyDuplicate(restName, prev));
  });

  // Only fall back to unfiltered list if dedup removed ALL items (empty day)
  return {
    ...newDay,
    schedule: dedupedActivities.length > 0 ? dedupedActivities : newActivities,
    meals: dedupedMeals.length > 0 ? dedupedMeals : newDay.meals,
  };
}
